/**
 * WhitelistSection — Gestion de la whitelist de pruebas productivas
 *
 * Permite agregar y remover clientes que pueden acceder al motor de credito
 * en ambiente productivo mientras la funcionalidad esta en pruebas.
 * Feature flag WHITELIST_ENABLED en backend controla el comportamiento del motor.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Search, X, Trash2, AlertTriangle, UserPlus, Clock, ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { useWhitelist } from '../../hooks/useWhitelist';
import type { SearchClientsForWhitelistItem, WhitelistItem, NaturalLevel } from '@/types/whitelist.types';

// ─── Debounce util ───────────────────────────────────────────────────────────

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

// ─── Helpers de formato ──────────────────────────────────────────────────────

function formatFecha(isoString: string): string {
  try {
    return new Intl.DateTimeFormat('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(new Date(isoString));
  } catch {
    return isoString;
  }
}

function truncateId(id: string, maxLen = 16): string {
  return id.length > maxLen ? `${id.slice(0, maxLen)}…` : id;
}

// ─── Nivel natural: badge + tooltip para decidir 'graduaciones' ─────────────

type NaturalLevelMeta = { label: string; tone: string; hint: string };
const NATURAL_LEVEL_META: Record<NaturalLevel, NaturalLevelMeta> = {
  PLATINUM: {
    label: 'PLATINUM',
    tone: 'bg-violet-500/15 text-violet-700 dark:text-violet-300 border border-violet-500/30',
    hint: 'Bona-fide PLATINUM: calificaria solo (nivel mayor que GOLD). Candidato a salir del whitelist.',
  },
  GOLD: {
    label: 'GOLD',
    tone: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30',
    hint: 'Bona-fide GOLD: ya calificaria solo. Candidato a salir del whitelist.',
  },
  SILVER: {
    label: 'SILVER',
    tone: 'bg-slate-400/20 text-slate-700 dark:text-slate-300 border border-slate-400/40',
    hint: 'Calificaria como SILVER. El whitelist lo esta subiendo a GOLD.',
  },
  BRONZE: {
    label: 'BRONZE',
    tone: 'bg-orange-500/15 text-orange-700 dark:text-orange-300 border border-orange-500/30',
    hint: 'Calificaria como BRONZE. El whitelist lo esta subiendo a GOLD.',
  },
  NONE: {
    label: 'NO CALIFICA',
    tone: 'bg-rose-500/15 text-rose-700 dark:text-rose-300 border border-rose-500/30',
    hint: 'Pasa elegibilidad pero score/meses no alcanzan minimo. Depende 100% del whitelist.',
  },
  NOT_ELIGIBLE: {
    label: 'NO ELEGIBLE',
    tone: 'bg-rose-500/15 text-rose-700 dark:text-rose-300 border border-rose-500/30',
    hint: 'Hard gates fallan (falta fiado.mx o fiado.us, u otros).',
  },
  NO_PROFILE: {
    label: 'SIN PROFILE',
    tone: 'bg-slate-500/15 text-slate-600 dark:text-slate-400 border border-slate-500/30',
    hint: 'Aun no tiene CreditFinancialProfile_GT (P1 no lo ha procesado).',
  },
};

// ─── Capacidad del whitelist: 3 chips (ocupados / disponibles / cap) ───────

/**
 * Cap del whitelist de pruebas productivas por partner. Espejo del backend
 * (credit-engine-business/src/domain/managers/WhitelistManager.ts::WHITELIST_CAP).
 * Si cambia aqui hay que cambiarlo tambien alla — la validacion dura vive
 * en el backend, esta constante solo es para el display de la UI.
 */
const WHITELIST_CAP = 30;

function WhitelistCapacityChips({ activeCount }: { activeCount: number }) {
  const remaining = Math.max(0, WHITELIST_CAP - activeCount);
  const almostFull = remaining <= 3;
  const full = remaining === 0;
  return (
    <div className="flex items-center gap-1.5">
      <span
        className="inline-flex items-center rounded-md border border-blue-500/30 bg-blue-500/10 px-2 py-0.5 text-[11px] font-medium text-blue-700 dark:text-blue-300"
        title="Clientes actualmente activos en la whitelist"
      >
        Ocupados <span className="ml-1 font-semibold tabular-nums">{activeCount}</span>
      </span>
      <span
        className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium tabular-nums ${
          full
            ? 'border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-300'
            : almostFull
              ? 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300'
              : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
        }`}
        title={full ? 'Whitelist llena — no se pueden agregar mas clientes' : 'Cupos disponibles para agregar clientes'}
      >
        Disponibles <span className="ml-1 font-semibold">{remaining}</span>
      </span>
      <span
        className="inline-flex items-center rounded-md border border-border bg-muted/60 px-2 py-0.5 text-[11px] font-medium text-muted-foreground"
        title="Limite maximo de clientes activos en la whitelist"
      >
        Cap <span className="ml-1 font-semibold tabular-nums">{WHITELIST_CAP}</span>
      </span>
    </div>
  );
}

function NaturalLevelBadge({ level }: { level: NaturalLevel | undefined }) {
  if (!level) return <span className="text-xs text-muted-foreground">—</span>;
  const meta = NATURAL_LEVEL_META[level];
  return (
    <span
      className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold ${meta.tone}`}
      title={meta.hint}
    >
      {meta.label}
    </span>
  );
}

// ─── Autocomplete de busqueda ────────────────────────────────────────────────

interface ClientSearchProps {
  onSelect: (item: SearchClientsForWhitelistItem) => void;
  searchClients: (q: string) => Promise<{ data: SearchClientsForWhitelistItem[] }>;
  disabled?: boolean;
}

function ClientSearch({ onSelect, searchClients, disabled }: ClientSearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchClientsForWhitelistItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const debouncedQuery = useDebounce(query, 300);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (debouncedQuery.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    setIsSearching(true);
    searchClients(debouncedQuery)
      .then((res) => {
        setResults(res.data ?? []);
        setOpen(true);
      })
      .catch(() => setResults([]))
      .finally(() => setIsSearching(false));
  }, [debouncedQuery, searchClients]);

  // Cierra el dropdown si se hace clic fuera
  useEffect(() => {
    function handleMousedown(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleMousedown);
    return () => document.removeEventListener('mousedown', handleMousedown);
  }, []);

  function handleSelect(item: SearchClientsForWhitelistItem) {
    onSelect(item);
    setQuery('');
    setResults([]);
    setOpen(false);
  }

  function handleClear() {
    setQuery('');
    setResults([]);
    setOpen(false);
  }

  return (
    <div ref={wrapperRef} className="relative w-full max-w-md">
      <div className="relative flex items-center">
        <Search size={16} className="absolute left-3 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar cliente por nombre o ID..."
          disabled={disabled}
          className="w-full pl-9 pr-9 py-2 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
        />
        {query && (
          <button
            type="button"
            onClick={handleClear}
            aria-label="Limpiar busqueda"
            className="absolute right-3 text-muted-foreground hover:text-foreground"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {open && results.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full bg-popover border border-border rounded-md shadow-lg max-h-56 overflow-y-auto">
          {results.map((item) => (
            <li key={item.directoryId}>
              <button
                type="button"
                className="w-full text-left px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground flex items-center justify-between gap-2"
                onClick={() => handleSelect(item)}
              >
                <span>
                  {item.name}
                  <span className="ml-2 font-mono text-xs text-muted-foreground">
                    {truncateId(item.directoryId)}
                  </span>
                </span>
                {item.hasActiveCredit && (
                  <span title="Ya tiene credito activo" className="text-amber-500 flex-shrink-0">
                    <AlertTriangle size={13} />
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      {open && !isSearching && results.length === 0 && debouncedQuery.length >= 2 && (
        <div className="absolute z-50 mt-1 w-full bg-popover border border-border rounded-md shadow-md px-3 py-2 text-sm text-muted-foreground">
          Sin resultados para "{debouncedQuery}"
        </div>
      )}
    </div>
  );
}

// ─── Fila de la tabla ────────────────────────────────────────────────────────

interface RowProps {
  item: WhitelistItem;
  showHistoric: boolean;
  onRemove: (directoryId: string, displayName: string) => void;
  isRemoving: boolean;
}

function WhitelistRow({ item, showHistoric, onRemove, isRemoving }: RowProps) {
  const isRemoved = item.isActive === 'NO';

  return (
    <tr className={isRemoved ? 'opacity-50' : ''}>
      <td className="px-4 py-3 text-sm">
        {item.displayName}
        {isRemoved && (
          <span className="ml-2 text-xs text-muted-foreground">(removido)</span>
        )}
      </td>
      <td className="px-4 py-3">
        <span className="font-mono text-xs text-muted-foreground">{item.directoryId}</span>
      </td>
      <td className="px-4 py-3">
        <NaturalLevelBadge level={item.naturalLevel} />
      </td>
      <td className="px-4 py-3 text-sm text-foreground">{formatFecha(item.addedAt)}</td>
      <td className="px-4 py-3 text-xs text-muted-foreground truncate max-w-[200px]" title={item.addedBy}>{item.addedBy}</td>
      {showHistoric && (
        <>
          <td className="px-4 py-3 text-sm">
            {item.removedAt
              ? <span className="text-foreground">{formatFecha(item.removedAt)}</span>
              : <span className="text-muted-foreground">—</span>}
          </td>
          <td className="px-4 py-3 text-xs text-muted-foreground truncate max-w-[200px]" title={item.removedBy ?? undefined}>
            {item.removedBy ?? '—'}
          </td>
        </>
      )}
      <td className="px-4 py-3 text-right">
        {!isRemoved && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRemove(item.directoryId, item.displayName)}
            disabled={isRemoving}
            aria-label={`Remover a ${item.displayName}`}
          >
            <Trash2 size={14} />
          </Button>
        )}
      </td>
    </tr>
  );
}

// ─── Componente principal ────────────────────────────────────────────────────

export function WhitelistSection() {
  const {
    items,
    activeCount,
    isLoading,
    isError,
    refetch,
    includeInactive,
    setIncludeInactive,
    addToWhitelist,
    removeFromWhitelist,
    isAdding,
    isRemoving,
    searchClients,
  } = useWhitelist();

  // Estado del autocomplete
  const [selectedClient, setSelectedClient] = useState<SearchClientsForWhitelistItem | null>(null);

  // Estado del filtro de busqueda en la tabla
  const [tableFilter, setTableFilter] = useState('');

  // Modal de confirmacion de borrado
  const [deleteTarget, setDeleteTarget] = useState<{ directoryId: string; displayName: string } | null>(null);

  // Resetea cliente seleccionado tras agregar
  const handleAdd = useCallback(() => {
    if (!selectedClient) return;
    addToWhitelist(
      { directoryId: selectedClient.directoryId, displayName: selectedClient.name },
      {
        onSuccess: () => setSelectedClient(null),
      },
    );
  }, [selectedClient, addToWhitelist]);

  const handleRemoveRequest = useCallback((directoryId: string, displayName: string) => {
    setDeleteTarget({ directoryId, displayName });
  }, []);

  const handleRemoveConfirm = useCallback(() => {
    if (!deleteTarget) return;
    removeFromWhitelist(deleteTarget.directoryId, {
      onSuccess: () => setDeleteTarget(null),
      onError: () => setDeleteTarget(null),
    });
  }, [deleteTarget, removeFromWhitelist]);

  // Sort state — la tabla es click-para-ordenar por columna. Click en la
  // misma columna alterna asc/desc; click en otra columna la selecciona en asc.
  type SortKey = 'name' | 'directoryId' | 'addedAt' | 'removedAt';
  const [sortKey, setSortKey] = useState<SortKey>('addedAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const handleSort = useCallback((key: SortKey) => {
    if (key === sortKey) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }, [sortKey]);

  // Filtro + sort combinados en un solo memo para no recalcular en cada render.
  const filteredItems = useMemo(() => {
    const q = tableFilter.toLowerCase();
    const filtered = items.filter((item) => {
      if (!q) return true;
      return (
        item.displayName.toLowerCase().includes(q) ||
        item.directoryId.toLowerCase().includes(q)
      );
    });

    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      let av: string;
      let bv: string;
      switch (sortKey) {
        case 'name':
          av = (a.displayName || '').toLowerCase();
          bv = (b.displayName || '').toLowerCase();
          break;
        case 'directoryId':
          av = a.directoryId.toLowerCase();
          bv = b.directoryId.toLowerCase();
          break;
        case 'addedAt':
          av = a.addedAt || '';
          bv = b.addedAt || '';
          break;
        case 'removedAt':
          av = a.removedAt || '';
          bv = b.removedAt || '';
          break;
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [items, tableFilter, sortKey, sortDir]);

  const SortableHeader = ({ label, sortKeyFor, align = 'left' }: { label: string; sortKeyFor: SortKey; align?: 'left' | 'right' }) => {
    const isActive = sortKey === sortKeyFor;
    return (
      <th className={`px-4 py-3 font-medium ${align === 'right' ? 'text-right' : 'text-left'}`}>
        <button
          type="button"
          onClick={() => handleSort(sortKeyFor)}
          className={`inline-flex items-center gap-1 uppercase tracking-wide hover:text-foreground transition-colors ${isActive ? 'text-foreground' : ''}`}
        >
          {label}
          {isActive && (sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
        </button>
      </th>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header con chips de ocupacion (activos / disponibles / cap) */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-base font-semibold">Clientes en whitelist</h2>
          <WhitelistCapacityChips activeCount={activeCount} />
        </div>
      </div>

      {/* Barra de agregar cliente */}
      <div className="rounded-md border border-border bg-card p-4 space-y-3">
        <p className="text-sm font-medium text-foreground">Agregar cliente</p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[240px]">
            {selectedClient ? (
              <div className="flex items-center gap-2 px-3 py-2 border border-input rounded-md bg-muted text-sm">
                <span className="flex-1">
                  {selectedClient.name}
                  <span className="ml-2 font-mono text-xs text-muted-foreground">
                    {truncateId(selectedClient.directoryId)}
                  </span>
                  {selectedClient.hasActiveCredit && (
                    <span className="ml-2 text-amber-600 text-xs">
                      <AlertTriangle size={12} className="inline mr-1" />
                      ya tiene credito activo
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  onClick={() => setSelectedClient(null)}
                  aria-label="Quitar seleccion"
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <ClientSearch
                onSelect={setSelectedClient}
                searchClients={searchClients}
                disabled={isAdding}
              />
            )}
          </div>
          <Button
            size="sm"
            disabled={!selectedClient || isAdding}
            onClick={handleAdd}
          >
            <UserPlus size={14} className="mr-1.5" />
            {isAdding ? 'Agregando...' : 'Agregar'}
          </Button>
        </div>
      </div>

      {/* Tabla de clientes */}
      <div className="rounded-md border border-border overflow-hidden">
        {/* Barra de busqueda en tabla */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-muted/30">
          <div className="relative flex items-center flex-1 max-w-xs">
            <Search size={14} className="absolute left-3 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              value={tableFilter}
              onChange={(e) => setTableFilter(e.target.value)}
              placeholder="Filtrar por nombre o ID..."
              className="w-full pl-8 pr-8 py-1.5 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
            />
            {tableFilter && (
              <button
                type="button"
                onClick={() => setTableFilter('')}
                aria-label="Limpiar filtro"
                className="absolute right-2 text-muted-foreground hover:text-foreground"
              >
                <X size={13} />
              </button>
            )}
          </div>
        </div>

        {/* Contenido */}
        {isLoading && (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">
            Cargando whitelist...
          </div>
        )}

        {isError && !isLoading && (
          <div className="px-4 py-8 text-center space-y-2">
            <p className="text-sm text-destructive">Error al cargar la whitelist.</p>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              Reintentar
            </Button>
          </div>
        )}

        {!isLoading && !isError && filteredItems.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">
            {tableFilter ? 'Sin resultados para ese filtro.' : 'La whitelist esta vacia.'}
          </div>
        )}

        {!isLoading && !isError && filteredItems.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs text-muted-foreground">
                <tr>
                  <SortableHeader label="Nombre" sortKeyFor="name" />
                  <SortableHeader label="Directory ID" sortKeyFor="directoryId" />
                  <th className="px-4 py-3 text-left font-medium uppercase tracking-wide" title="Nivel que el motor le asignaria SIN el whitelist. Ayuda a decidir a quien 'graduar' del whitelist.">
                    Nivel natural
                  </th>
                  <SortableHeader label="Agregado" sortKeyFor="addedAt" />
                  <th className="px-4 py-3 text-left font-medium uppercase tracking-wide">Agregado por</th>
                  {includeInactive && (
                    <>
                      <SortableHeader label="Removido" sortKeyFor="removedAt" />
                      <th className="px-4 py-3 text-left font-medium uppercase tracking-wide">Removido por</th>
                    </>
                  )}
                  <th className="px-4 py-3 text-right font-medium uppercase tracking-wide">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredItems.map((item) => (
                  <WhitelistRow
                    key={item.directoryId}
                    item={item}
                    showHistoric={includeInactive}
                    onRemove={handleRemoveRequest}
                    isRemoving={isRemoving}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Toggle historico */}
        <div className="flex items-center gap-2 px-4 py-3 border-t border-border bg-muted/20">
          <button
            type="button"
            onClick={() => setIncludeInactive(!includeInactive)}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <Clock size={13} />
            {includeInactive ? 'Ocultar historico' : 'Ver historico (removidos)'}
          </button>
        </div>
      </div>

      {/* Modal de confirmacion de borrado */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Remover de la whitelist"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-foreground">
            Esta seguro de remover a{' '}
            <span className="font-medium">{deleteTarget?.displayName}</span> de la whitelist?
            El cliente perdera acceso al motor de credito en pruebas de inmediato.
          </p>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDeleteTarget(null)}
              disabled={isRemoving}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleRemoveConfirm}
              disabled={isRemoving}
            >
              {isRemoving ? 'Removiendo...' : 'Remover'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
